<template>
  <div class="editpage">
    <div class="image-selector-container">
      <image-selector
        v-if="model"
        v-model="image"
        class="view"
        :ratio="1"
        :defaultImage="imagePath"
        placeholder="/img/placeholders/character.png"
        @input="change"
      />
    </div>
    <character-form v-if="model" :novel-id="novelId" v-model="model" />
    <button-default class="submit" :disabled="!model || !model.name || saving" @click="submit">{{
      $t("characterEditPage.updateButton")
    }}</button-default>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import CharacterForm from "@/components/organisms/CharacterForm.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import ImageSelector from "@/components/molecules/ImageSelector.vue";
import { submitImage } from "@/lib/image-save";
import { Character, UpdateCharacter } from "@/lib/models";
import { cloneDeep } from "lodash";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { CharacterForm, ButtonDefault, ImageSelector },
  props: {
    novelId: String,
    characterId: String,
  },
  data() {
    return {
      model: null,
      image: null,
      saving: false,
      unbindShortcutKeys: null,
    };
  },
  async created() {
    const { novelId, characterId } = this;
    await this.$store.dispatch("characterModule/initialize", novelId);
    const { character } = this;
    if (!character) {
      this.$router.replace({
        name: "characters",
        params: characterId ? { novelId, characterId } : { novelId },
      });
      return;
    }
    const { name, detail, additionalColumn, image } = character;
    this.image = image;
    this.model = { name, detail, additionalColumn };

    // 保存ボタンのショートカットキーの有効化と解除関数の取得
    this.unbindShortcutKeys = handleShortcutKeys([
      { trigger: TriggerKey.Meta, keys: ["s"], callback: this.submit },
      { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.submit },
    ]);
  },
  beforeDestroy() {
    // コンポーネントが破棄される前に保存ボタンのショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();
  },
  computed: {
    character() {
      return cloneDeep(this.$store.getters["characterModule/character"](this.characterId));
    },
    imagePath() {
      const { novelId, characterId, image } = this;
      if (!image) return null;
      if (image.startsWith("file:")) {
        return `file:novels/${novelId}/characters/${characterId}.jpg`;
      }
      if (image.startsWith("preset:")) {
        return image;
      }
      return null;
    },
  },
  methods: {
    change() {
      this.setModified(true);
    },
    async submit() {
      if (!this.model || !this.model.name || this.saving) {
        return;
      }
      try {
        this.saving = true;
        const { model } = this;
        const { novelId, characterId, image } = this;
        const { additionalColumn } = model;
        model.additionalColumn = additionalColumn.filter((item) => item.label || item.content);

        const updateCharacterInput: UpdateCharacter = {
          novelId,
          characterId,
          image,
          ...model,
        };
        await this.$store.dispatch("characterModule/updateCharacter", updateCharacterInput);
        await submitImage(`novels/${novelId}/characters/${characterId}.jpg`);

        this.$notify({
          title: this.$t("common.saved"),
          type: "success",
        });

        this.setModified(false);

        this.$router.push({
          name: "characters",
          params: { novelId, characterId },
        });
      } finally {
        this.saving = false;
      }
    },
  },
});

interface Props {
  novelId: string;
  characterId: string;
}

interface Data {
  model: {
    name: string;
    detail: any[];
    additionalColumn: any[];
  } | null;
  image: string | null;
  saving: boolean;
  unbindShortcutKeys: (() => void) | null;
}

interface Computed {
  character: Character;
  imagePath: string | null;
}

interface Methods {
  change(): void;
  submit(): Promise<void>;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.editpage {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  padding: 0 12.5%;
  padding-bottom: 60px;
  @include mq(sp) {
    padding: 0;
    padding-bottom: 50px;
  }

  .image-selector-container {
    display: flex;
    align-items: flex-end;
  }
}
.content-main {
  margin: $margin-m;
}
.button {
  margin: 15px auto;
  padding: 30px auto;
  border-radius: var(--radius-rounded);
  font-weight: var(--font-weight-bold);
  width: 350px;

  @include mq(sp) {
    margin: 15px 30px;
    padding: 15px 30px;
    width: auto;
  }
}
.submit {
  position: relative;
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border-color: var(--color-primary);

  @include hover-overlay;

  &:disabled {
    background-color: var(--color-surface-muted);
    color: var(--color-inactive);
    border: solid 1px var(--color-border);
    &::after {
      display: none;
    }
  }
}
</style>
